import axios from 'axios';
import { API_URL } from '../config';
import { refreshToken } from './auth';

// Create axios instance with base URL
// Normalize baseURL to ensure it includes protocol and host, not a port-only string
let baseURL = API_URL;
if (baseURL && baseURL.startsWith(':')) {
    baseURL = `http://127.0.0.1${baseURL}`;
}
const api = axios.create({
    baseURL,
    timeout: 300000, // 5 minute timeout
    headers: {
        'Content-Type': 'application/json'
    }
});

// Helper function to build query parameters for pagination
export const buildQueryParams = (params = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
            if (Array.isArray(value)) {
                value.forEach(item => queryParams.append(key, item));
            } else {
                queryParams.append(key, value);
            }
        }
    });
    
    return queryParams.toString();
};

// Normalize helpers for list and paginated responses
export const normalizeList = (data) => {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
};

export const normalizePage = (data) => {
    if (data && Array.isArray(data.results)) return data;
    return {
        results: Array.isArray(data) ? data : [],
        count: Array.isArray(data) ? data.length : (data?.count || 0),
        next: data?.next ?? null,
        previous: data?.previous ?? null,
        page: data?.page ?? 1,
        total_pages: data?.total_pages ?? (data?.count && data?.page_size ? Math.ceil(data.count / data.page_size) : 1),
        page_size: data?.page_size ?? (Array.isArray(data) ? data.length : 0),
    };
};

// Add request interceptor for authentication
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Don't override Content-Type for FormData
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor for token refresh
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Don't intercept token-related endpoints to avoid infinite loops
        const isTokenEndpoint = originalRequest.url?.includes('/token/');
        
        // Handle 401 Unauthorized errors
        if (error.response?.status === 401 && !originalRequest._retry && !isTokenEndpoint) {
            originalRequest._retry = true;

            try {
                const response = await refreshToken();
                if (response.access) {
                    originalRequest.headers.Authorization = `Bearer ${response.access}`;
                    return api(originalRequest);
                } else {
                    throw new Error('No access token in refresh response');
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                
                // Clear all authentication data
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('userData');
                
                // Redirect to login page
                window.location.href = '/login';
                // Return a never-resolving promise so the calling component
                // doesn't briefly render an error page while the browser
                // navigates to /login.
                return new Promise(() => {});
            }
        }

        return Promise.reject(error);
    }
);

export default api; 